import { supabase } from './supabase'

export function normalizePhone(phone: string) {
  return phone.replace(/[^\d]/g, '')
}

export interface CustomerProfile {
  id: number
  full_name: string
  phone: string
  email?: string | null
  address_text?: string | null
  lat?: number | null
  lng?: number | null
  maps_url?: string | null
  terms_accepted_at?: string | null
}

export async function getCustomerByPhone(
  phone: string,
): Promise<CustomerProfile | null> {
  if (!phone?.trim()) return null
  const cleanPhone = normalizePhone(phone)
  if (!cleanPhone) return null

  const { data, error } = await supabase
    .from('customers')
    .select(
      `
      id,
      full_name,
      phone,
      email,
      terms_accepted_at,
      customer_addresses (
        id,
        address_text,
        lat,
        lng,
        maps_url,
        is_default
      )
    `,
    )
    .eq('phone', cleanPhone)
    .limit(1)
    .maybeSingle()

  if (error) {
    console.error('[CUSTOMER] getCustomerByPhone error:', error)
    return null
  }

  if (!data) return null

  const defaultAddress =
    data.customer_addresses?.find((a: any) => a.is_default) ||
    data.customer_addresses?.[0]

  return {
    id: data.id,
    full_name: data.full_name,
    phone: data.phone,
    email: data.email,
    terms_accepted_at: data.terms_accepted_at,
    address_text: defaultAddress?.address_text ?? null,
    lat: defaultAddress?.lat ?? null,
    lng: defaultAddress?.lng ?? null,
    maps_url: defaultAddress?.maps_url ?? null,
  }
}

export async function upsertCustomerProfile(input: {
  fullName: string
  phone: string
  email?: string
  addressText?: string
  mapsUrl?: string
  lat?: number | null
  lng?: number | null
  termsAccepted?: boolean
}) {
  const cleanedPhone = normalizePhone(input.phone)
  const cleanedName = input.fullName.trim()

  const { data: existingCustomer, error: findError } = await supabase
    .from('customers')
    .select('id, full_name, phone, email, terms_accepted_at')
    .eq('phone', cleanedPhone)
    .limit(1)
    .maybeSingle()

  if (findError) {
    throw findError
  }

  let customerId: number

  if (!existingCustomer) {
    const { data: insertedCustomer, error: insertError } = await supabase
      .from('customers')
      .insert({
        full_name: cleanedName,
        phone: cleanedPhone,
        email: input.email || null,
        terms_accepted_at: input.termsAccepted
          ? new Date().toISOString()
          : null,
      })
      .select('id')
      .single()

    if (insertError) throw insertError
    customerId = insertedCustomer.id
  } else {
    customerId = existingCustomer.id

    const updatePayload: Record<string, any> = {
      updated_at: new Date().toISOString(),
    }

    if (existingCustomer.full_name !== cleanedName) {
      updatePayload.full_name = cleanedName
    }
    if (input.email && existingCustomer.email !== input.email) {
      updatePayload.email = input.email
    }
    if (input.termsAccepted && !existingCustomer.terms_accepted_at) {
      updatePayload.terms_accepted_at = new Date().toISOString()
    }

    if (Object.keys(updatePayload).length > 1) {
      const { error: updateError } = await supabase
        .from('customers')
        .update(updatePayload)
        .eq('id', customerId)

      if (updateError) throw updateError
    }
  }

  if (input.addressText?.trim()) {
    const { data: existingAddresses, error: addressFindError } = await supabase
      .from('customer_addresses')
      .select('id, address_text, maps_url, is_default')
      .eq('customer_id', customerId)
      .eq('is_default', true)

    if (addressFindError) throw addressFindError

    if (!existingAddresses || existingAddresses.length === 0) {
      const { error: addressInsertError } = await supabase
        .from('customer_addresses')
        .insert({
          customer_id: customerId,
          address_text: input.addressText.trim(),
          lat: input.lat ?? null,
          lng: input.lng ?? null,
          maps_url: input.mapsUrl || null,
          is_default: true,
        })

      if (addressInsertError) throw addressInsertError
    } else {
      const defaultAddress = existingAddresses[0]

      const { error: addressUpdateError } = await supabase
        .from('customer_addresses')
        .update({
          address_text: input.addressText.trim(),
          lat: input.lat ?? null,
          lng: input.lng ?? null,
          maps_url: input.mapsUrl || null,
          is_default: true,
        })
        .eq('id', defaultAddress.id)

      if (addressUpdateError) throw addressUpdateError
    }
  }

  return { customerId }
}

// ─── localStorage fallback for customer data ─────────────────
const CUSTOMER_LOCAL_KEY = 'noiza_customer_data'

export interface LocalCustomerData {
  full_name: string
  phone: string
  email?: string
  address_text?: string
  maps_url?: string
  terms_accepted: boolean
}

export function saveCustomerToLocal(data: LocalCustomerData): void {
  try {
    localStorage.setItem(CUSTOMER_LOCAL_KEY, JSON.stringify(data))
    localStorage.setItem('noiza_customer_phone', normalizePhone(data.phone))
  } catch (e) {
    console.error('[CUSTOMER] saveCustomerToLocal error:', e)
  }
}

export function getCustomerFromLocal(): LocalCustomerData | null {
  try {
    const raw = localStorage.getItem(CUSTOMER_LOCAL_KEY)
    if (!raw) return null
    return JSON.parse(raw) as LocalCustomerData
  } catch (e) {
    console.error('[CUSTOMER] getCustomerFromLocal error:', e)
    return null
  }
}
